/*
  Shared scroll behavior for every section.
  Load this ONCE in theme.liquid, right before </body>:
    {{ 'scroll-effects.js' | asset_url | script_tag }}

  Any section can opt an element into these behaviors just by adding
  a class — no extra JS needed per section:
    .scale-img   -> grows as it nears the top of the viewport,
                    shrinks back as it scrolls away (either direction).
                    Zoom intensity defaults to a subtle 14% but can be
                    overridden per-section by setting the CSS custom
                    property --scale-amount (e.g. 0.5 for a 50% zoom) on
                    the element or any ancestor — it inherits normally.
    .fill-text   -> wraps its words into lines and reveals them
                    gray-to-ink one full line at a time on scroll
    .count-up    -> starts at 0 and animates up to data-target once
                    the element scrolls into view (triggers once)
    .scroll-hide -> fades out while scrolling down, fades back in
                    while scrolling up, and is always shown again near
                    the very top of the page. Used for the header logo,
                    but any element can opt in.

  Also, for the multi-step booking flow specifically (see any of
  sign-up.liquid / otp-verify.liquid / booking.liquid /
  pickup-location.liquid / booking-confirmation.liquid for the full
  picture):
    [data-step]          -> makes a section one step in a wizard; only
                             the active step is visible (see the step
                             wizard section further down this file)
    .step-next/.step-prev -> buttons that advance/return within the wizard
    [data-otp-box]        -> auto-advances focus to the next box as each
                             digit is typed (front-end UX only, does not
                             validate/send/check a real code)
    [data-location-select] on a card, with data-location-name /
                             data-location-address -> clicking selects
                             it (single-select) and remembers the choice
                             in sessionStorage
    [data-location-display] containing [data-location-display-name] /
                             [data-location-display-address] -> gets
                             filled in from whatever was selected above,
                             even on a totally different section/step

  Also, for the New CTA section's Shopify Forms block:
    [data-form-slot] containing [data-form-slot-content] -> shows
                             placeholder bars (.is-waiting) until the
                             app-drawn form has real height, so the
                             heading doesn't jump when it appears.

  Also, for the flip cards in the Stacked Cards section:
    .flip-card + .flip-card-front + .flip-card-back -> clicking/tapping
                             the front face adds .flipped to the
                             .flip-card ancestor (a CSS 3D rotateY
                             transform does the actual flip); clicking
                             anywhere on the back EXCEPT its .book-now-pill
                             link removes .flipped again. Escape also
                             flips back any currently-flipped card, as a
                             keyboard-accessible fallback.

  Also measures the real rendered height of .header-wrapper and writes
  it to the CSS custom property --header-height on load, resize, and
  section reload — mirroring what Dawn's own sticky-header component
  does for its own markup, so anything elsewhere in the theme that
  reserves space based on --header-height gets an accurate value rather
  than a stale/default one.

  Also wires up two click-driven interactions, via event delegation on
  document (so they keep working after the theme editor re-renders a
  section, with no re-init needed):
    .hamburger + .mobile-nav  -> hamburger toggles open/closed, and any
                                 link inside .mobile-nav closes it again
    .faq-item > .faq-q        -> accordion toggle; opening one item
                                 closes any other open item in the same
                                 .faq-list container
*/
(function(){
  if (window.__scrollEffectsInit) return;
  window.__scrollEffectsInit = true;

  // Shared state, refreshed by init() on load and on shopify:section:load.
  //
  // IMPORTANT: every listener that init() depends on (scroll, resize,
  // shopify:section:load) is bound ONCE, down below this block -- never
  // inside init(). init() used to bind its own scroll + resize listeners
  // AND register a fresh shopify:section:load listener each time it ran,
  // and that listener calls init() again. So every section reload in the
  // theme editor DOUBLED the number of listeners (2, 3, 5, 9, 17, 33, 65
  // ...), each one re-running the whole setup, until the editor ground to
  // a halt. Don't move them back inside init().
  let scrollHideEls = [];
  let scaleItems = [];
  let visibleScaleItems = new Set();
  let scaleObserver = null;
  let fillTextEls = [];
  let ticking = false;
  let lastWidth = window.innerWidth;
  let resizeTimer = null;

  // Wrap each word of a .fill-text element in its own span, once.
  function wrapFillText(el){
    if (el.dataset.wrapped) return;
    const words = el.textContent.trim().split(/\s+/);
    el.innerHTML = words.map(w => '<span class="fw">' + w + '</span>').join(' ');
    el.dataset.wrapped = 'true';
  }

  // Group word-spans into visual lines based on rendered top offset.
  function measureLines(el){
    const spans = Array.from(el.querySelectorAll('.fw'));
    const lines = [];
    let currentTop = null;
    spans.forEach(s=>{
      const top = s.offsetTop;
      if (currentTop === null || Math.abs(top - currentTop) > 2){
        lines.push([]);
        currentTop = top;
      }
      lines[lines.length - 1].push(s);
    });
    el._lines = lines;
  }

  function measureAll(){
    fillTextEls.forEach(measureLines);
  }

  // Sections can set --scale-amount (inherited via CSS) to control how
  // dramatic the zoom is; defaults to the original subtle 0.14. Read once
  // per image in init() -- NOT every scroll frame -- since getComputedStyle
  // forces a style recalculation and this used to run for every image on
  // the page on every frame.
  function readScaleAmount(el){
    const raw = getComputedStyle(el).getPropertyValue('--scale-amount').trim();
    return parseFloat(raw) || 0.14;
  }

  function update(){
    const vh = window.innerHeight;

    // Only images currently near the viewport (tracked by the
    // IntersectionObserver in init()) -- not every image on the page.
    visibleScaleItems.forEach(item=>{
      const rect = item.el.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      let progress = 1 - (center / vh);
      progress = Math.max(0, Math.min(1, progress));
      const scale = 1 + progress * item.amount;
      item.el.style.transform = 'scale(' + scale.toFixed(3) + ')';
    });

    fillTextEls.forEach(el=>{
      const rect = el.getBoundingClientRect();
      const start = vh * 0.85;
      const end = vh * 0.35;
      let progress = (start - rect.top) / (start - end);
      progress = Math.max(0, Math.min(1, progress));

      const lines = el._lines || [];
      const L = lines.length || 1;
      lines.forEach((line, i)=>{
        const lit = progress >= i / L;
        // Only touch the DOM when a line's state actually changed.
        if (line._lit === lit) return;
        line._lit = lit;
        line.forEach(span => span.classList.toggle('lit', lit));
      });
    });

    ticking = false;
  }

  function onScroll(){
    if (!ticking){
      requestAnimationFrame(update);
      ticking = true;
    }
  }

  // Resize fires constantly on mobile (the URL bar showing/hiding counts),
  // so debounce it, and only re-measure text lines when the WIDTH actually
  // changed -- a height-only change can't re-wrap any text.
  function onResize(){
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(()=>{
      setHeaderHeight();
      if (window.innerWidth !== lastWidth){
        lastWidth = window.innerWidth;
        measureAll();
      }
      onScroll();
    }, 150);
  }

  // Measure the real rendered header height and write it to
  // --header-height. Dawn's own sticky-header component does the same
  // thing for its markup; this keeps the variable accurate for our
  // header too, so anything elsewhere in the theme that reserves space
  // based on --header-height doesn't fall back to a stale value.
  function setHeaderHeight(){
    const headerEl = document.querySelector('.header-wrapper');
    if (!headerEl) return;
    document.documentElement.style.setProperty('--header-height', headerEl.offsetHeight + 'px');
  }

  // Count-up numbers: start at 0, animate to data-target once scrolled into view.
  function animateCount(el){
    if (el.dataset.counted) return;
    el.dataset.counted = 'true';
    const target = parseFloat(el.dataset.target || el.textContent) || 0;
    const duration = 1200;
    const startTime = performance.now();
    function step(now){
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      el.textContent = Math.round(eased * target);
      if (t < 1){
        requestAnimationFrame(step);
      } else {
        el.textContent = target;
      }
    }
    requestAnimationFrame(step);
  }

  // Form placeholder (New CTA section): the Shopify Forms app block draws
  // its form with its own script AFTER the page loads, so the area is empty
  // for a moment and the centered heading then jumps as the form appears.
  // The section reserves the space in CSS; this shows placeholder bars in it
  // (".is-waiting") until the form's content actually has height, or 15s
  // pass. It only measures our own wrapper's height, so it makes no
  // assumptions about how the app draws its form, and it adds no
  // document/window listeners (safe to re-run on every section reload).
  // This changes how the wait LOOKS -- it does not make the form load faster.
  function initFormSlots(){
    document.querySelectorAll('[data-form-slot]').forEach(container=>{
      if (container.dataset.slotInit) return;
      container.dataset.slotInit = 'true';

      const slot = container.querySelector('[data-form-slot-content]');
      if (!slot || !slot.firstElementChild) return; // nothing rendered (e.g. editor, no block added yet)
      // Fail open: with neither observer available, show no placeholder at all.
      if (!('ResizeObserver' in window) && !('MutationObserver' in window)) return;

      const READY_HEIGHT = 80; // px -- the app wrapper alone can have ~25px of padding
      const observers = [];
      let timer = null;

      function finish(){
        container.classList.remove('is-waiting');
        container.classList.add('is-ready');
        container.removeAttribute('aria-busy');
        observers.forEach(o => o.disconnect());
        clearTimeout(timer);
      }
      function check(){
        if (slot.getBoundingClientRect().height >= READY_HEIGHT) finish();
      }

      container.classList.add('is-waiting');
      container.setAttribute('aria-busy', 'true');
      if ('MutationObserver' in window){
        const mo = new MutationObserver(check);
        mo.observe(slot, { childList:true, subtree:true, attributes:true });
        observers.push(mo);
      }
      if ('ResizeObserver' in window){
        const ro = new ResizeObserver(check);
        ro.observe(slot);
        observers.push(ro);
      }
      timer = setTimeout(finish, 15000);
      check();
    });
  }

  // Safe to call repeatedly (initial load, and every section reload in
  // the theme editor): it only refreshes element lists and observers, it
  // never binds a document/window listener.
  function init(){
    scrollHideEls = document.querySelectorAll('.scroll-hide');
    fillTextEls = document.querySelectorAll('.fill-text');
    fillTextEls.forEach(wrapFillText);
    measureAll();

    // Scale-on-scroll images: read each one's amount once, and track
    // which are near the viewport so off-screen ones cost nothing.
    if (scaleObserver) scaleObserver.disconnect();
    visibleScaleItems = new Set();
    scaleItems = Array.from(document.querySelectorAll('.scale-img')).map(el=>{
      const item = { el, amount: readScaleAmount(el) };
      el._scaleItem = item;
      return item;
    });
    if ('IntersectionObserver' in window){
      scaleObserver = new IntersectionObserver((entries)=>{
        entries.forEach(entry=>{
          const item = entry.target._scaleItem;
          if (!item) return;
          if (entry.isIntersecting) visibleScaleItems.add(item);
          else visibleScaleItems.delete(item);
        });
        onScroll();
      }, { rootMargin:'150px 0px' });
      scaleItems.forEach(item => scaleObserver.observe(item.el));
    } else {
      scaleItems.forEach(item => visibleScaleItems.add(item));
    }

    setHeaderHeight();
    update();

    const countEls = document.querySelectorAll('.count-up:not([data-counted])');
    if ('IntersectionObserver' in window){
      const countObserver = new IntersectionObserver((entries)=>{
        entries.forEach(entry=>{
          if (entry.isIntersecting){
            animateCount(entry.target);
            countObserver.unobserve(entry.target);
          }
        });
      }, { threshold:0.4 });
      countEls.forEach(el => countObserver.observe(el));
    } else {
      countEls.forEach(el => { el.textContent = el.dataset.target || el.textContent; });
    }

    initFormSlots();
  }

  // Bound ONCE (see the note at the top of this block).
  window.addEventListener('scroll', onScroll, { passive:true });
  window.addEventListener('resize', onResize);

  // Re-scan when Shopify section editor re-renders a section
  // (theme editor swaps the section's DOM but doesn't reload the page).
  document.addEventListener('shopify:section:load', ()=>{
    document.querySelectorAll('.fill-text').forEach(el=>{ delete el.dataset.wrapped; });
    init();
    document.querySelectorAll('.faq-tabs').forEach(tabsEl=>{
      const activeTab = tabsEl.querySelector('.faq-tab.active') || tabsEl.querySelector('.faq-tab');
      if (activeTab) applyFaqTabFilter(tabsEl, activeTab.dataset.category);
    });
  });

  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Hamburger menu: toggle .open on the button and its associated .mobile-nav.
  document.addEventListener('click', (e)=>{
    const btn = e.target.closest('.hamburger');
    if (btn){
      const nav = btn.closest('nav');
      const mobileNav = nav && nav.nextElementSibling && nav.nextElementSibling.classList.contains('mobile-nav')
        ? nav.nextElementSibling
        : null;
      const isOpen = btn.classList.toggle('open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (mobileNav) mobileNav.classList.toggle('open', isOpen);
      return;
    }

    // Clicking a link inside the open mobile nav closes it again.
    const mobileLink = e.target.closest('.mobile-nav a');
    if (mobileLink){
      const mobileNav = mobileLink.closest('.mobile-nav');
      const nav = mobileNav && mobileNav.previousElementSibling;
      const hamburger = nav && nav.querySelector('.hamburger');
      if (mobileNav) mobileNav.classList.remove('open');
      if (hamburger){
        hamburger.classList.remove('open');
        hamburger.setAttribute('aria-expanded', 'false');
      }
    }
  });

  // FAQ accordion: opening one item closes any other open item in the
  // same list, matching a standard single-open accordion pattern.
  document.addEventListener('click', (e)=>{
    const question = e.target.closest('.faq-q');
    if (!question) return;
    const item = question.closest('.faq-item');
    if (!item) return;
    const list = item.closest('.faq-list') || item.parentElement;
    const wasOpen = item.classList.contains('open');
    list.querySelectorAll('.faq-item.open').forEach(other => other.classList.remove('open'));
    if (!wasOpen) item.classList.add('open');
  });

  // FAQ category tabs: clicking a tab shows only the questions whose
  // data-category matches it, hides the rest, and closes any open
  // question so switching topics doesn't leave a stale answer expanded.
  function applyFaqTabFilter(tabsEl, category){
    const section = tabsEl.closest('.faq-section');
    if (!section) return;
    const list = section.querySelector('.faq-list');
    if (!list) return;
    list.querySelectorAll('.faq-item').forEach(item=>{
      const matches = item.dataset.category === category;
      item.style.display = matches ? '' : 'none';
      item.classList.remove('open');
    });
  }

  document.addEventListener('click', (e)=>{
    const tab = e.target.closest('.faq-tab');
    if (!tab) return;
    const tabsEl = tab.closest('.faq-tabs');
    if (!tabsEl) return;
    tabsEl.querySelectorAll('.faq-tab').forEach(t => t.classList.remove('active'));
    tab.classList.add('active');
    applyFaqTabFilter(tabsEl, tab.dataset.category);
  });

  // Apply the initial filter on load (and on section reload in the
  // editor) so only the first tab's questions show before any click.
  document.querySelectorAll('.faq-tabs').forEach(tabsEl=>{
    const activeTab = tabsEl.querySelector('.faq-tab.active') || tabsEl.querySelector('.faq-tab');
    if (activeTab) applyFaqTabFilter(tabsEl, activeTab.dataset.category);
  });

  // Scroll-direction visibility (.scroll-hide): fades out while scrolling
  // down, fades back in while scrolling up, and is always shown again
  // near the very top of the page. Bound once here rather than inside
  // init() so repeated shopify:section:load calls don't stack duplicate
  // scroll listeners -- scrollHideEls itself is still refreshed by
  // init(), so elements added after a section reload are picked up.
  let lastScrollHideY = window.pageYOffset || document.documentElement.scrollTop;
  const SCROLL_HIDE_THRESHOLD = 6; // px of movement before reacting, avoids jitter
  const SCROLL_HIDE_TOP_ZONE = 40; // px from the top where it always stays visible

  window.addEventListener('scroll', ()=>{
    const currentY = window.pageYOffset || document.documentElement.scrollTop;
    const delta = currentY - lastScrollHideY;

    if (currentY <= SCROLL_HIDE_TOP_ZONE){
      scrollHideEls.forEach(el => el.classList.remove('scroll-hide--hidden'));
    } else if (Math.abs(delta) > SCROLL_HIDE_THRESHOLD){
      const scrollingDown = delta > 0;
      scrollHideEls.forEach(el => el.classList.toggle('scroll-hide--hidden', scrollingDown));
    }
    lastScrollHideY = currentY < 0 ? 0 : currentY;
  }, { passive:true });

  // ------------------------------------------------------------
  // Step wizard: turns a stack of sections into a one-at-a-time
  // multi-step flow (e.g. sign-up -> OTP -> book a slot -> location
  // -> confirmation), all living on a single page/template.
  //
  // How to use it, from any section:
  //   - Give the section's outer wrapper: data-step="1" (2, 3, ...)
  //   - Add a button inside it with class "step-next" to advance to
  //     the next data-step number, or "step-prev" to go back.
  //   - Optionally add data-step-goto="3" on a button to jump straight
  //     to a specific step instead of just next/prev.
  //
  // Only the current step is visible at a time (the rest are
  // display:none via the .step-hidden class) -- this is a real
  // one-at-a-time wizard, not a scroll-to-section effect. Progress is
  // remembered in sessionStorage (per-tab, cleared when the tab
  // closes) so a refresh mid-flow doesn't dump the visitor back to
  // step 1.
  //
  // Deliberately NOT included: any attempt to detect when a
  // third-party app's own overlay/modal (e.g. a booking calendar) has
  // been completed. There's no reliable way to hook into another
  // app's internal UI from here -- a step that hands off to one
  // should include its own manual "Continue" button for the visitor
  // to click once they're done with that app's popup.
  // ------------------------------------------------------------
  const STEP_STORAGE_KEY = 'arke-step-wizard-current-step';

  function getStepEls(){
    return Array.from(document.querySelectorAll('[data-step]'));
  }

  function goToStep(stepNumber){
    const steps = getStepEls();
    if (steps.length === 0) return;

    steps.forEach(el => {
      const isTarget = String(el.dataset.step) === String(stepNumber);
      el.classList.toggle('step-hidden', !isTarget);
      if (isTarget) el.scrollIntoView({ behavior:'smooth', block:'start' });
    });

    try {
      sessionStorage.setItem(STEP_STORAGE_KEY, String(stepNumber));
    } catch (e) {
      // sessionStorage unavailable (private browsing, etc.) -- fine,
      // the wizard still works, it just won't survive a refresh.
    }
  }

  function initStepWizard(){
    const steps = getStepEls();
    if (steps.length === 0) return;

    const stepNumbers = steps
      .map(el => parseFloat(el.dataset.step))
      .filter(n => !isNaN(n))
      .sort((a, b) => a - b);

    let startStep = stepNumbers[0];
    try {
      const saved = sessionStorage.getItem(STEP_STORAGE_KEY);
      if (saved !== null && stepNumbers.includes(parseFloat(saved))) {
        startStep = parseFloat(saved);
      }
    } catch (e) { /* ignore */ }

    goToStep(startStep);
  }

  document.addEventListener('click', (e)=>{
    const gotoBtn = e.target.closest('[data-step-goto]');
    if (gotoBtn){
      goToStep(gotoBtn.dataset.stepGoto);
      return;
    }

    const nextBtn = e.target.closest('.step-next');
    if (nextBtn){
      const steps = getStepEls();
      const stepNumbers = steps
        .map(el => parseFloat(el.dataset.step))
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b);
      const current = steps.find(el => !el.classList.contains('step-hidden'));
      const currentNum = current ? parseFloat(current.dataset.step) : stepNumbers[0];
      const idx = stepNumbers.indexOf(currentNum);
      if (idx > -1 && idx < stepNumbers.length - 1){
        goToStep(stepNumbers[idx + 1]);
      }
      return;
    }

    const prevBtn = e.target.closest('.step-prev');
    if (prevBtn){
      const steps = getStepEls();
      const stepNumbers = steps
        .map(el => parseFloat(el.dataset.step))
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b);
      const current = steps.find(el => !el.classList.contains('step-hidden'));
      const currentNum = current ? parseFloat(current.dataset.step) : stepNumbers[0];
      const idx = stepNumbers.indexOf(currentNum);
      if (idx > 0){
        goToStep(stepNumbers[idx - 1]);
      }
    }
  });

  // ------------------------------------------------------------
  // OTP box auto-advance: typing a digit in one box automatically
  // moves focus to the next box; Backspace on an empty box moves
  // focus back to the previous one. Purely a front-end UX nicety --
  // does not validate or submit anything (see otp-verify.liquid's
  // comment for why real verification can't happen here).
  // ------------------------------------------------------------
  document.addEventListener('input', (e)=>{
    const box = e.target.closest('[data-otp-box]');
    if (!box) return;
    box.value = box.value.replace(/[^0-9]/g, '').slice(0, 1);
    if (box.value){
      const next = box.nextElementSibling;
      if (next && next.matches('[data-otp-box]')) next.focus();
    }
  });

  document.addEventListener('keydown', (e)=>{
    const box = e.target.closest('[data-otp-box]');
    if (!box) return;
    if (e.key === 'Backspace' && !box.value){
      const prev = box.previousElementSibling;
      if (prev && prev.matches('[data-otp-box]')) prev.focus();
    }
  });

  // ------------------------------------------------------------
  // Location card selection: clicking an available card in a
  // [data-location-group] marks it selected (single-select, like a
  // radio group) and remembers the choice in sessionStorage so the
  // Confirmation step (or any other step) can read it back and
  // display it, even though they're different sections on the page.
  // ------------------------------------------------------------
  const LOCATION_STORAGE_KEY = 'arke-selected-location';

  function selectLocationCard(card){
    const group = card.closest('[data-location-group]');
    if (group){
      group.querySelectorAll('[data-location-select]').forEach(el => el.classList.remove('location-selected'));
    }
    card.classList.add('location-selected');
    const name = card.dataset.locationName || '';
    const address = card.dataset.locationAddress || '';
    try {
      sessionStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({ name, address }));
    } catch (e) { /* ignore, private browsing etc. */ }
    applySelectedLocationDisplay();
  }

  function applySelectedLocationDisplay(){
    let saved = null;
    try {
      const raw = sessionStorage.getItem(LOCATION_STORAGE_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch (e) { /* ignore */ }
    if (!saved) return;

    document.querySelectorAll('[data-location-display]').forEach(displayEl => {
      const nameEl = displayEl.querySelector('[data-location-display-name]');
      const addressEl = displayEl.querySelector('[data-location-display-address]');
      if (nameEl && saved.name) nameEl.textContent = saved.name;
      if (addressEl && saved.address) addressEl.textContent = saved.address;
    });
  }

  document.addEventListener('click', (e)=>{
    const card = e.target.closest('[data-location-select]');
    if (card) selectLocationCard(card);
  });

  document.addEventListener('keydown', (e)=>{
    const card = e.target.closest('[data-location-select]');
    if (card && (e.key === 'Enter' || e.key === ' ')){
      e.preventDefault();
      selectLocationCard(card);
    }
  });

  // ------------------------------------------------------------
  // Flip cards (Stacked Cards section): clicking/tapping the front
  // face flips the card to its back; clicking anywhere on the back
  // EXCEPT the "Book now" link flips it back to the front. Clicking
  // the link itself just follows the link normally, no flip.
  //
  // Keyboard note: the back face is a plain container (it holds its
  // own separate link, so it can't itself be a <button> the way the
  // front is -- a button can't contain another interactive element).
  // That means "click the back to flip back" has no direct keyboard
  // equivalent, so Escape is wired up as a fallback: pressing it while
  // any card is flipped flips it back, a well-understood pattern for
  // reversing/closing an expanded state without needing a visible
  // dedicated control.
  // ------------------------------------------------------------
  document.addEventListener('click', (e)=>{
    const front = e.target.closest('.flip-card-front');
    if (front){
      const card = front.closest('.flip-card');
      if (card) card.classList.add('flipped');
      return;
    }
    const back = e.target.closest('.flip-card-back');
    if (back && !e.target.closest('.book-now-pill')){
      const card = back.closest('.flip-card');
      if (card) card.classList.remove('flipped');
    }
  });

  document.addEventListener('keydown', (e)=>{
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.flip-card.flipped').forEach(card => card.classList.remove('flipped'));
  });

  applySelectedLocationDisplay();

  initStepWizard();
  document.addEventListener('shopify:section:load', ()=>{
    initStepWizard();
    applySelectedLocationDisplay();
  });
})();